import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';

/**
 * Upcoming fundraising deadlines & actions for the admin home dashboard.
 *
 * Reuses the same GET /admin/fundraising/dashboard fetch (and identical query
 * key) as FundraisingPipelineWidget so React Query dedupes to one request.
 */
interface FundraisingDashboardResponse {
  upcoming: Array<{ id: string; kind: string; title: string; due_at: string; status: string }>;
}

export default function FundraisingDeadlinesWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'fundraising', 'dashboard'],
    queryFn: () => api.get<FundraisingDashboardResponse>('/admin/fundraising/dashboard'),
    staleTime: 60000,
  });

  const upcoming = data?.upcoming ?? [];

  return (
    <WidgetShell
      title="Upcoming Deadlines"
      icon={<CalendarClock />}
      to="/admin/fundraising/calendar"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && upcoming.length === 0}
      emptyMessage="Nothing on the calendar in the near term."
    >
      {data && upcoming.length > 0 && (
        <ul className="divide-y divide-line">
          {upcoming.slice(0, 5).map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{item.title}</p>
                <p className="text-xs text-stone-500">{formatDate(item.due_at)}</p>
              </div>
              <span className="shrink-0 rounded-md bg-stone-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-stone-600">
                {item.kind.replace(/_/g, ' ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}
